import React, { useCallback, useMemo, useState } from 'react';

import { useLiveQuery } from 'dexie-react-hooks';
import { ChevronDown, Search, X } from 'lucide-react';

import { apiDelete, apiPatch } from '../../lib/api_v2';
import db from '../../lib/db';
import { logError } from '../../lib/logger';
import { cn } from '../../lib/utils';

import { useRouteWorkspace } from '../../contexts/RouteWorkspaceContext';
import useLeaveWorkspace from '../../hooks/useLeaveWorkspace';
import Flexicon from './Flexicon';

// Exported so member-count consumers (e.g. WorkspaceOptionsModal's summary row)
// share one definition of "active".
export const ACTIVE_MEMBER_ROLES = ['admin', 'member'];

const memberPillClasses =
  'flex h-9 shrink-0 cursor-pointer items-center justify-center rounded-full px-3 text-cursed disabled:cursor-not-allowed disabled:opacity-50';

const MemberRoleControl = ({ member, canEditRole, isChangingRole, onRoleSelectChange }) => {
  const roleLabel = member.role === 'deactivated' ? 'Deactivated' : member.role === 'admin' ? 'Admin' : 'Member';

  if (!canEditRole) {
    return <span className="text-cursed text-gray-500 dark:text-neutral-500">{roleLabel}</span>;
  }

  return (
    <span data-app-region="none" className="relative inline-flex cursor-pointer items-center gap-1">
      <span className="text-cursed text-gray-500 dark:text-neutral-500">{roleLabel}</span>
      <ChevronDown className="size-4 shrink-0 text-gray-500 dark:text-neutral-500" strokeWidth={1.5} absoluteStrokeWidth />
      <select
        value={member.role}
        onChange={onRoleSelectChange}
        disabled={isChangingRole}
        className="absolute inset-0 size-full cursor-pointer text-cursed opacity-0 disabled:cursor-not-allowed"
      >
        <option value="admin">Admin</option>
        <option value="member">Member</option>
      </select>
    </span>
  );
};

// leave/isLeaving come from the panel's single useLeaveWorkspace — per-row calls
// would each fire the same API call path.
const MemberRow = ({ member, workspaceId, profileId, isAdmin, onActionComplete, leave, isLeaving }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [isChangingRole, setIsChangingRole] = useState(false);

  const isSelf = member.id === profileId;
  const isActiveMember = ACTIVE_MEMBER_ROLES.includes(member.role);
  const canEditRole = isAdmin && isActiveMember;

  const handleRoleSelectChange = useCallback(
    async (event) => {
      const newRole = event.target.value;
      if (newRole === member.role) return;

      // Demoting yourself immediately revokes your own admin access, and only an admin
      // can hand it back — which may be nobody once you step down. Confirm first.
      if (isSelf && member.role === 'admin' && newRole === 'member') {
        const confirmed = window.confirm(
          'Are you sure you want to make yourself a member?\n\nYou will immediately lose admin access to this chat, and an admin will have to promote you back.',
        );
        if (!confirmed) return;
      }

      setIsChangingRole(true);
      try {
        const response = await apiPatch(`/workspaces/${workspaceId}/members/${member.id}`, { profile: { role: newRole } });
        const data = await response.json();

        if (data.ok) {
          onActionComplete?.();
        } else {
          alert('Could not update member role. Please try again.');
          logError('MembersPanel', 'Failed to update member role', data.errors);
        }
      } catch (error) {
        alert('Could not update member role. Please try again.');
        logError('MembersPanel', 'Failed to update member role', error);
      } finally {
        setIsChangingRole(false);
      }
    },
    [isSelf, member.id, member.role, workspaceId, onActionComplete],
  );

  const handleRemoveMember = useCallback(async () => {
    if (!window.confirm('Are you sure you want to remove this member?\n\nThey will need to be invited again to join.')) return;

    setIsProcessing(true);
    try {
      const response = await apiDelete(`/workspaces/${workspaceId}/members/${member.id}`);
      const data = await response.json();

      if (data.ok) {
        onActionComplete?.();
      } else {
        alert('Failed to remove member, please try again.');
        logError('MembersPanel', 'Failed to remove member', data.errors);
      }
    } catch (error) {
      alert('Failed to remove member, please try again.');
      logError('MembersPanel', 'Failed to remove member', error);
    } finally {
      setIsProcessing(false);
    }
  }, [member.id, workspaceId, onActionComplete]);

  const leaveButton = (
    <button
      type="button"
      data-app-region="none"
      onClick={leave}
      disabled={isLeaving}
      className={cn(memberPillClasses, 'w-18 border border-red-500 text-red-500 hover:bg-red-500/5')}
    >
      Leave
    </button>
  );

  return (
    <div className="flex h-12 w-full items-center gap-3">
      <span className="inline-flex aspect-square size-12 overflow-hidden rounded-full border-[0.5px] border-black/10">
        <Flexicon resource={member} size="size-12" rounded="rounded-full" className="size-12" />
      </span>

      <span className="min-w-0 flex-1 truncate text-[13px] leading-[18px] font-medium text-black dark:text-neutral-100">
        {member.full_name}
      </span>

      <span className="flex w-25 shrink-0 items-center">
        <MemberRoleControl
          member={member}
          canEditRole={canEditRole}
          isChangingRole={isChangingRole}
          onRoleSelectChange={handleRoleSelectChange}
        />
      </span>

      {/* Always-rendered action column so role labels align across rows with and without a button */}
      <span className="flex w-20 shrink-0 items-center justify-end">
        {isSelf && isActiveMember && leaveButton}
        {!isSelf && isAdmin && isActiveMember && (
          <button
            type="button"
            data-app-region="none"
            onClick={handleRemoveMember}
            disabled={isProcessing}
            className={cn(memberPillClasses, 'border-[0.5px] border-white bg-gray-200 text-black hover:bg-gray-300 dark:border-neutral-700 dark:bg-neutral-800 dark:text-neutral-100 dark:hover:bg-neutral-700')}
          >
            Remove
          </button>
        )}
      </span>
    </div>
  );
};

const MembersPanel = () => {
  const { workspace, workspaceId, profileId, profile } = useRouteWorkspace();
  const isAdmin = profile?.role === 'admin';
  const [query, setQuery] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  const { leave, isLeaving } = useLeaveWorkspace();

  const members = useLiveQuery(
    async () => {
      if (!workspaceId) return [];
      const allProfiles = await db.profiles.where('workspace_id').equals(workspaceId).toArray();
      return allProfiles.sort((a, b) => {
        if (a.id === profileId && a.role !== 'deactivated') return -1;
        if (b.id === profileId && b.role !== 'deactivated') return 1;
        if (a.role === 'deactivated' && b.role !== 'deactivated') return 1;
        if (a.role !== 'deactivated' && b.role === 'deactivated') return -1;
        if (a.role === 'admin' && b.role !== 'admin') return -1;
        if (a.role !== 'admin' && b.role === 'admin') return 1;
        return (a.full_name || '').localeCompare(b.full_name || '');
      });
    },
    [refreshKey, workspaceId, profileId],
    [],
  );

  const activeMemberCount = useMemo(() => members.filter((member) => ACTIVE_MEMBER_ROLES.includes(member.role)).length, [members]);

  const trimmedQuery = query.trim().toLowerCase();
  const visibleMembers = useMemo(() => {
    if (!trimmedQuery) return members;
    return members.filter((member) => (member.full_name || '').toLowerCase().includes(trimmedQuery));
  }, [members, trimmedQuery]);

  const handleQueryChange = useCallback((event) => setQuery(event.target.value), []);
  const handleClearQuery = useCallback(() => setQuery(''), []);
  const handleActionComplete = useCallback(() => setRefreshKey((prev) => prev + 1), []);

  if (!workspace || !profile) return null;

  return (
    <div className="w-full pt-5 pb-2">
      <div className="group flex h-12 w-full items-center gap-4 rounded-lg border sm:border-[0.5px] border-white bg-gray-50 px-4 hover:bg-white dark:border-neutral-800 dark:bg-neutral-900">
        <Search
          className={cn(
            'size-4 shrink-0',
            query
              ? 'text-black dark:text-neutral-100'
              : 'text-gray-300 group-focus-within:text-black dark:text-neutral-600 dark:group-focus-within:text-neutral-100',
          )}
          strokeWidth={1.5}
          absoluteStrokeWidth
        />
        <input
          type="text"
          value={query}
          onChange={handleQueryChange}
          placeholder="Search"
          autoComplete="off"
          data-1p-ignore="true"
          className="w-full bg-transparent text-[15px] leading-5 text-black placeholder:text-gray-300 focus:outline-none dark:text-neutral-100 dark:placeholder:text-neutral-600"
        />
        {query ? (
          <button
            type="button"
            data-app-region="none"
            onClick={handleClearQuery}
            aria-label="Clear search"
            className="flex shrink-0 cursor-pointer items-center justify-center text-black focus:outline-none dark:text-neutral-100"
          >
            <X className="size-4" strokeWidth={1.5} absoluteStrokeWidth />
          </button>
        ) : null}
      </div>

      <div className="mt-5 flex w-full flex-col rounded-lg border-[0.5px] border-white bg-gray-50 p-4 dark:border-neutral-800 dark:bg-neutral-900">
        <div className="flex h-5 items-center justify-between">
          <span className="text-sm leading-5 font-medium text-black dark:text-neutral-100">
            {activeMemberCount} {activeMemberCount === 1 ? 'Member' : 'Members'}
          </span>
        </div>

        <div className="mt-4 h-[0.5px] w-full bg-gray-200 dark:bg-neutral-800" />

        <div className="mt-4 flex w-full flex-col gap-7">
          {visibleMembers.map((member) => (
            <MemberRow
              key={member.id}
              member={member}
              workspaceId={workspaceId}
              profileId={profileId}
              isAdmin={isAdmin}
              onActionComplete={handleActionComplete}
              leave={leave}
              isLeaving={isLeaving}
            />
          ))}
          {visibleMembers.length === 0 && (
            <p className="text-cursed text-gray-500 dark:text-neutral-500">No members match your search.</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default MembersPanel;
